import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Plus, Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

const DATA_TYPES = ['INT', 'BIGINT', 'VARCHAR', 'TEXT', 'DATE', 'DATETIME', 'BOOLEAN', 'JSON', 'FLOAT', 'DOUBLE', 'DECIMAL'];

export const TableEditDialog = ({ open, onOpenChange, table, onSave }) => {
  const [tableName, setTableName] = useState('');
  const [columns, setColumns] = useState([]);

  useEffect(() => {
    if (table) {
      setTableName(table.data.name || '');
      setColumns(table.data.columns || []);
    }
  }, [table]);

  const handleAddColumn = () => {
    const newColumn = {
      id: uuidv4(),
      name: `column_${columns.length + 1}`,
      data_type: 'VARCHAR',
      length: 255,
      is_primary_key: false,
      is_auto_increment: false,
      is_nullable: true,
      default_value: ''
    };
    setColumns([...columns, newColumn]);
  };

  const handleUpdateColumn = (columnId, field, value) => {
    setColumns(columns.map(col => 
      col.id === columnId ? { ...col, [field]: value } : col
    ));
  };

  const handleDeleteColumn = (columnId) => {
    setColumns(columns.filter(col => col.id !== columnId));
  };

  const handleSave = () => {
    if (table) {
      onSave(table.id, { name: tableName, columns });
      onOpenChange(false);
    }
  };

  if (!table) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]" data-testid="table-edit-dialog">
        <DialogHeader>
          <DialogTitle style={{fontFamily: 'Space Grotesk'}}>Edit Table</DialogTitle>
          <DialogDescription>
            Edit nama tabel dan kolom-kolomnya
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <div className="p-4 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="table-name">Nama Tabel</Label>
              <Input
                id="table-name"
                data-testid="table-name-input"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                placeholder="table_name"
                style={{fontFamily: 'JetBrains Mono'}}
              />
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Columns</Label>
                <Button
                  data-testid="add-column-btn"
                  size="sm"
                  onClick={handleAddColumn}
                  className="h-8 hover:-translate-y-[1px] transition-transform duration-200"
                >
                  <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} />
                  Add Column
                </Button>
              </div>

              {columns.map((column, index) => (
                <div
                  key={column.id}
                  data-testid={`column-editor-${column.id}`}
                  className="p-3 border border-border rounded-md space-y-3 bg-accent/30"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium" style={{fontFamily: 'JetBrains Mono'}}>Column {index + 1}</span>
                    <Button
                      data-testid={`delete-column-${column.id}`}
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => handleDeleteColumn(column.id)}
                    >
                      <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs">Name</Label>
                    <Input
                      data-testid={`column-name-${column.id}`}
                      value={column.name}
                      onChange={(e) => handleUpdateColumn(column.id, 'name', e.target.value)}
                      className="h-8 text-xs"
                      style={{fontFamily: 'JetBrains Mono'}}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label className="text-xs">Data Type</Label>
                      <Select
                        value={column.data_type}
                        onValueChange={(value) => handleUpdateColumn(column.id, 'data_type', value)}
                      >
                        <SelectTrigger data-testid={`column-type-${column.id}`} className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DATA_TYPES.map(type => (
                            <SelectItem key={type} value={type} className="text-xs">{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {['VARCHAR', 'CHAR'].includes(column.data_type) && (
                      <div className="space-y-2">
                        <Label className="text-xs">Length</Label>
                        <Input
                          data-testid={`column-length-${column.id}`}
                          type="number"
                          value={column.length || ''}
                          onChange={(e) => handleUpdateColumn(column.id, 'length', parseInt(e.target.value) || null)}
                          className="h-8 text-xs"
                        />
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        data-testid={`column-pk-${column.id}`}
                        checked={column.is_primary_key}
                        onCheckedChange={(checked) => handleUpdateColumn(column.id, 'is_primary_key', checked)}
                        id={`pk-${column.id}`}
                      />
                      <Label htmlFor={`pk-${column.id}`} className="text-xs cursor-pointer">Primary Key</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        data-testid={`column-ai-${column.id}`}
                        checked={column.is_auto_increment}
                        onCheckedChange={(checked) => handleUpdateColumn(column.id, 'is_auto_increment', checked)}
                        id={`ai-${column.id}`}
                      />
                      <Label htmlFor={`ai-${column.id}`} className="text-xs cursor-pointer">Auto Increment</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        data-testid={`column-nullable-${column.id}`}
                        checked={column.is_nullable}
                        onCheckedChange={(checked) => handleUpdateColumn(column.id, 'is_nullable', checked)}
                        id={`null-${column.id}`}
                      />
                      <Label htmlFor={`null-${column.id}`} className="text-xs cursor-pointer">Nullable</Label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs">Default Value</Label>
                    <Input
                      data-testid={`column-default-${column.id}`}
                      value={column.default_value || ''}
                      onChange={(e) => handleUpdateColumn(column.id, 'default_value', e.target.value)}
                      placeholder="NULL"
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button
            data-testid="cancel-edit-btn"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            Batal
          </Button>
          <Button
            data-testid="save-table-btn"
            onClick={handleSave}
            className="hover:-translate-y-[1px] transition-transform duration-200 shadow-md"
          >
            Simpan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
